import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import PasswordRules from "pages/components/SignUp/PasswordRules";
import { Fragment, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { SignUpForm } from "types";
import { buttonVariants } from "utils/muiTailwindStyles";

import DisclaimerModal from "./DisclaimerModal";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import TermsAndConditionsModal from "./TermsAndConditionsModal";

type LegalModalType = "terms" | "privacy" | "disclaimer" | null;

interface Props {
  readonly onPreviousStep: () => void;
  readonly isSubmitting: boolean;
  readonly isValid: boolean;
}

function ProviderInformation({ onPreviousStep, isSubmitting, isValid }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openModal, setOpenModal] = useState<LegalModalType>(null);
  const { colors } = useCelleraTheme();

  const {
    control,
    formState: { errors },
  } = useFormContext<SignUpForm>();

  return (
    <Fragment>
      <Box className="text-center space-y-6">
        <Typography
          variant="h5"
          component="h5"
          className="text-f22 leading-7 text-surface-dark"
        >
          Sign Up
        </Typography>
        <Box>
          <Typography variant="body2" className="text-black text-base px-4">
            Enter your provider information to complete your account setup.
          </Typography>
        </Box>
      </Box>
      <Box className="space-y-6">
        <Controller
          name="email"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              id="email"
              label="Account Email"
              variant="outlined"
              className="w-full"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...field}
            />
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <TextField
                {...field}
                id="password"
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                error={!!error}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: colors.text.secondary }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    maxLength: 20,
                  },
                }}
              />
              {/*password rules */}
              <PasswordRules password={field.value} />
            </>
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id="confirm-password"
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        sx={{ color: colors.text.secondary }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />

        <Box className="flex gap-5">
          <Controller
            name="firstName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                id="first-name"
                label="First Name"
                variant="outlined"
                className="w-full"
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />

          <Controller
            name="middleName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                id="middle-name"
                label="Middle Name"
                variant="outlined"
                className="w-full"
                error={!!errors.middleName}
                helperText={errors.middleName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                id="last-name"
                label="Last Name"
                variant="outlined"
                className="w-full"
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                slotProps={{
                  htmlInput: {
                    maxLength: 64,
                  },
                }}
                {...field}
              />
            )}
          />
        </Box>

        <Controller
          name="npi"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              id="npi"
              label="NPI"
              variant="outlined"
              className="w-full"
              error={!!errors.npi}
              helperText={
                errors.npi?.message ||
                "If you are a doctor/provider, you will need to enter your NPI here"
              }
              {...field}
            />
          )}
        />

        <Box className="text-center">
          <Typography variant="body2" sx={{ color: "#000" }}>
            By signing up, you have agreed to our{" "}
            <Link
              component="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal("terms");
              }}
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Terms & Conditions
            </Link>
            ,{" "}
            <Link
              component="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal("privacy");
              }}
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link
              component="button"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal("disclaimer");
              }}
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Disclaimer
            </Link>
            .
          </Typography>
        </Box>

        <Button
          type="submit"
          variant="contained"
          className={`${buttonVariants.primary.className} w-full text-base rounded-full py-3 disabled:text-surface-light disabled:opacity-40`}
          sx={{
            ...buttonVariants.primary.sx,
          }}
          disabled={!isValid}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Sign Up"
          )}
        </Button>
      </Box>

      <Button
        type="submit"
        variant="outlined"
        className="w-full h-14 font-medium rounded-[100px] text-[#464653]"
        sx={{
          fontSize: "16px",
          textTransform: "none",
        }}
        onClick={onPreviousStep}
      >
        Go Back
      </Button>

      {/* Legal Modals */}
      <TermsAndConditionsModal
        open={openModal === "terms"}
        onClose={() => setOpenModal(null)}
      />
      <PrivacyPolicyModal
        open={openModal === "privacy"}
        onClose={() => setOpenModal(null)}
      />
      <DisclaimerModal
        open={openModal === "disclaimer"}
        onClose={() => setOpenModal(null)}
      />
    </Fragment>
  );
}

export default ProviderInformation;
